import { createFileRoute } from '@tanstack/react-router'
import { PencilIcon, Trash2Icon } from 'lucide-react'
import { useState } from 'react'

import type { MeetingGuest } from '@/lib/api/meeting-guest-api'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { useAuth } from '@/hooks/use-auth'
import {
    useDeleteMeetingGuest,
    useMeetingGuests,
    useUpdateMeetingGuest,
} from '@/hooks/use-meeting-guest-query'
import { useDebounced } from '@/hooks/use-debounced'
import { m } from '@/lib/paraglide/messages'

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/work-activities/guests',
)({
    component: MeetingGuestDirectoryPage,
})

/** Quyền duy nhất mở được màn này. */
const PERMISSION = 'meeting_guests.manage'

/** Trần độ dài tên và chức vụ, khớp với giới hạn của máy chủ. */
const MAX_FIELD_LENGTH = 255

/**
 * Gác quyền ngay tại trang.
 *
 * Mục menu có gác rồi nhưng đường dẫn thì ai gõ cũng vào được, và endpoint đọc
 * danh bạ chỉ đòi `work_activities.create` — thứ mà kinh doanh, kế toán, kho,
 * tài xế đều có. Không chặn ở đây thì họ thấy nguyên danh bạ kèm nút Sửa/Xoá,
 * bấm vào thì lần nào cũng ăn 403 mà không hiểu vì sao.
 */
export function MeetingGuestDirectoryPage() {
    const { hasPermission, arePermissionsLoaded } = useAuth()
    if (!arePermissionsLoaded) {
        return (
            <div className="text-muted-foreground p-4 text-sm md:p-6">
                {m.common_loading()}
            </div>
        )
    }
    if (!hasPermission(PERMISSION)) return <ForbiddenState />
    return <MeetingGuestDirectory />
}

function ForbiddenState() {
    return (
        <Card className="m-4 md:m-6">
            <CardContent className="space-y-2 py-12 text-center">
                <h2 className="text-lg font-semibold">
                    {m.meeting_guest_forbidden_title()}
                </h2>
                <p className="text-muted-foreground text-sm">
                    {m.meeting_guest_forbidden_desc()}
                </p>
            </CardContent>
        </Card>
    )
}

/**
 * Màn quản lý danh bạ khách mời.
 *
 * Cố tình KHÔNG có nút "Thêm mới": khách được thêm ngay lúc xếp lịch họp, đó là
 * lúc người dùng thật sự biết tên và chức vụ. Màn này chỉ để dọn dẹp — sửa tên
 * gõ sai và xoá người nhập nhầm.
 */
function MeetingGuestDirectory() {
    const [search, setSearch] = useState('')
    const debouncedSearch = useDebounced(search)
    const [editing, setEditing] = useState<MeetingGuest | null>(null)
    const [draftName, setDraftName] = useState('')
    const [draftTitle, setDraftTitle] = useState('')
    const [draftError, setDraftError] = useState('')

    const { data, isLoading, isError } = useMeetingGuests({
        search: debouncedSearch || undefined,
        per_page: 100,
    })
    const update = useUpdateMeetingGuest()
    const remove = useDeleteMeetingGuest()

    const rows = data?.data ?? []
    // Trang chỉ nạp một trang; quá trần là những người sau vô hình. Không nói
    // ra thì người thứ 101 trở đi biến mất mà không ai biết để đi tìm.
    const total = data?.meta?.total ?? rows.length
    const truncated = total > rows.length

    const startEdit = (guest: MeetingGuest) => {
        setEditing(guest)
        setDraftName(guest.full_name)
        setDraftTitle(guest.title)
        setDraftError('')
    }

    const saveEdit = async () => {
        if (!editing) return
        // Kiểm ngay tại đây để lỗi nằm CẠNH ô sai. Chỉ dựa vào toast của server
        // thì câu lỗi trôi mất sau vài giây mà ô nào sai vẫn không ai chỉ ra.
        if (!draftName.trim()) {
            setDraftError(m.meeting_guest_name_required())
            return
        }
        setDraftError('')
        try {
            await update.mutateAsync({
                id: editing.id,
                body: { full_name: draftName, title: draftTitle },
            })
            setEditing(null)
        } catch {
            // Thông báo đã do hook bắn ra toast; giữ ô sửa lại để không mất
            // những gì người dùng vừa gõ.
        }
    }

    return (
        <div className="space-y-4 page-pad">
            <div>
                <h1 className="text-lg font-semibold">
                    {m.meeting_guest_directory_title()}
                </h1>
                <p className="text-muted-foreground mt-1 max-w-3xl text-sm">
                    {m.meeting_guest_directory_subtitle()}
                </p>
            </div>

            <div className="max-w-sm">
                <label
                    htmlFor="guest-search"
                    className="mb-1 block text-sm font-medium"
                >
                    {m.meeting_guest_search_label()}
                </label>
                <Input
                    id="guest-search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={m.meeting_guest_search_placeholder()}
                />
            </div>

            {isLoading && (
                <p className="text-muted-foreground text-sm">
                    {m.work_activity_board_loading()}
                </p>
            )}
            {isError && (
                <p className="text-destructive text-sm">
                    {m.work_activity_board_load_error()}
                </p>
            )}

            {truncated && (
                <p className="text-muted-foreground text-sm">
                    {m.meeting_guest_shown_of_total({
                        shown: String(rows.length),
                        total: String(total),
                    })}
                </p>
            )}

            {!isLoading && !isError && rows.length === 0 && (
                <p className="text-muted-foreground text-sm">
                    {m.meeting_guest_empty_directory()}
                </p>
            )}

            {rows.length > 0 && (
                <ul className="divide-border divide-y rounded-md border">
                    {rows.map((guest) => (
                        <li key={guest.id} className="p-3">
                            {editing?.id === guest.id ? (
                                <div className="flex flex-wrap items-end gap-2">
                                    <div className="min-w-40 flex-1">
                                        <label
                                            htmlFor={`name-${guest.id}`}
                                            className="mb-1 block text-xs font-medium"
                                        >
                                            {m.meeting_guest_field_name()}
                                        </label>
                                        <Input
                                            id={`name-${guest.id}`}
                                            value={draftName}
                                            maxLength={MAX_FIELD_LENGTH}
                                            aria-invalid={
                                                draftError ? true : undefined
                                            }
                                            aria-describedby={
                                                draftError
                                                    ? `name-error-${guest.id}`
                                                    : undefined
                                            }
                                            onChange={(e) => {
                                                setDraftName(e.target.value)
                                                setDraftError('')
                                            }}
                                        />
                                        {draftError && (
                                            <p
                                                id={`name-error-${guest.id}`}
                                                className="text-destructive mt-1 text-xs"
                                            >
                                                {draftError}
                                            </p>
                                        )}
                                    </div>
                                    <div className="min-w-40 flex-1">
                                        <label
                                            htmlFor={`title-${guest.id}`}
                                            className="mb-1 block text-xs font-medium"
                                        >
                                            {m.meeting_guest_field_title()}
                                        </label>
                                        <Input
                                            id={`title-${guest.id}`}
                                            value={draftTitle}
                                            maxLength={MAX_FIELD_LENGTH}
                                            onChange={(e) =>
                                                setDraftTitle(e.target.value)
                                            }
                                        />
                                    </div>
                                    <Button
                                        type="button"
                                        onClick={() => void saveEdit()}
                                        disabled={update.isPending}
                                    >
                                        {m.common_save()}
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => setEditing(null)}
                                    >
                                        {m.action_cancel()}
                                    </Button>
                                </div>
                            ) : (
                                <div className="flex items-center gap-3">
                                    <div className="min-w-0 flex-1">
                                        <p className="truncate text-sm font-medium">
                                            {guest.full_name}
                                        </p>
                                        {guest.title && (
                                            <p className="text-muted-foreground truncate text-xs">
                                                {guest.title}
                                            </p>
                                        )}
                                    </div>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        aria-label={m.meeting_guest_edit()}
                                        onClick={() => startEdit(guest)}
                                    >
                                        <PencilIcon className="size-4" />
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        aria-label={m.meeting_guest_delete_confirm(
                                            { name: guest.full_name },
                                        )}
                                        disabled={remove.isPending}
                                        onClick={() => {
                                            if (
                                                window.confirm(
                                                    `${m.meeting_guest_delete_confirm({ name: guest.full_name })}\n\n${m.meeting_guest_delete_hint()}`,
                                                )
                                            ) {
                                                remove.mutate(guest.id)
                                            }
                                        }}
                                    >
                                        <Trash2Icon className="size-4" />
                                    </Button>
                                </div>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    )
}
